import { useState, useEffect, useMemo } from "react";
import { Eye, TrendingUp, TrendingDown, Activity, Zap, ArrowUpDown, BarChart3, RefreshCw, Plus, Search, Trash2, ExternalLink, Volume2 } from "lucide-react";
import { watchlistAPI, finnhubAPI } from "../services/api";
import { WatchlistCsvImport } from "./WatchlistCsvImport";

const REGION_MAP = {
  ".PA": "EU", ".DE": "EU", ".L": "EU", ".AS": "EU", ".CO": "EU", ".MC": "EU",
  ".T": "ASIA", ".KS": "ASIA", ".HK": "ASIA",
};
function getRegion(t) {
  for (const [suffix, region] of Object.entries(REGION_MAP)) {
    if (t.endsWith(suffix)) return region;
  }
  return "US";
}

function fmtVol(v) {
  if (!v) return "-";
  if (v >= 1e9) return `${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K`;
  return v.toString();
}

export default function WatchlistTab({ owner, livePrices, onAnalyze, onRefreshPrices, watchlist = [], onRemove, onAdd, onReload }) {
  const [sortBy, setSortBy] = useState("day_change");
  const [sortDir, setSortDir] = useState("desc");
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [addTicker, setAddTicker] = useState("");

  // Enrich watchlist with live data
  const enriched = useMemo(() => {
    return watchlist.map(w => {
      const lp = livePrices?.[w.ticker] || {};
      const dayPct = lp.dp || 0;
      const price = lp.c || w.entry_price || 0;
      const volRatio = lp.vol_ratio || null;
      const volume = lp.v || null;
      const rsi = w.rsi || null;
      const score = w.composite_score || w.score || 0;
      const signal = w.signal || "HOLD";
      const region = w.region || getRegion(w.ticker);

      // Momentum badges
      const badges = [];
      if (volRatio && volRatio >= 2.5) badges.push({ label: `VOL x${volRatio.toFixed(1)}`, color: "rose" });
      else if (volRatio && volRatio >= 1.5) badges.push({ label: `VOL x${volRatio.toFixed(1)}`, color: "amber" });
      if (rsi && rsi < 30) badges.push({ label: `RSI ${rsi.toFixed(0)}`, color: "emerald" });
      if (rsi && rsi > 70) badges.push({ label: `RSI ${rsi.toFixed(0)}`, color: "rose" });
      if (dayPct >= 5) badges.push({ label: "BREAKOUT", color: "emerald" });
      if (dayPct <= -5) badges.push({ label: "CRASH", color: "rose" });
      if (score >= 70) badges.push({ label: "STRONG", color: "emerald" });

      return { ...w, dayPct, price, volRatio, volume, rsi, score, signal, region, badges };
    });
  }, [watchlist, livePrices]);

  // Filter + Sort
  const filtered = useMemo(() => {
    let result = enriched;
    if (filter !== "ALL") result = result.filter(w => w.region === filter);
    if (search) result = result.filter(w => w.ticker.includes(search.toUpperCase()) || (w.name || "").toUpperCase().includes(search.toUpperCase()));
    result.sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case "day_change": va = a.dayPct; vb = b.dayPct; break;
        case "volume": va = a.volRatio || 0; vb = b.volRatio || 0; break;
        case "score": va = a.score; vb = b.score; break;
        case "rsi": va = a.rsi || 50; vb = b.rsi || 50; break;
        default: va = a.dayPct; vb = b.dayPct;
      }
      return sortDir === "desc" ? vb - va : va - vb;
    });
    return result;
  }, [enriched, filter, search, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const handleAdd = () => {
    if (addTicker.trim() && onAdd) {
      onAdd(addTicker.trim().toUpperCase());
      setAddTicker("");
    }
  };

  const stats = useMemo(() => {
    const up = enriched.filter(w => w.dayPct > 0).length;
    const down = enriched.filter(w => w.dayPct < 0).length;
    const hotVol = enriched.filter(w => (w.volRatio || 0) >= 2).length;
    const bestPct = enriched.length > 0 ? Math.max(...enriched.map(w => w.dayPct)) : 0;
    return { up, down, hotVol, bestPct };
  }, [enriched]);

  const SortHeader = ({ col, label }) => (
    <th className="py-2 px-1.5 font-medium cursor-pointer hover:text-white transition-colors select-none" onClick={() => toggleSort(col)}>
      <span className="flex items-center gap-0.5">
        {label}
        {sortBy === col && <ArrowUpDown className="w-2.5 h-2.5 text-violet-400" />}
      </span>
    </th>
  );

  return (
    <div className="space-y-4" data-testid="watchlist-tab">
      {/* Header + Stats */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-violet-400" />
          <h2 className="text-lg font-bold">Watchlist</h2>
          <span className="text-xs text-slate-400">{enriched.length} titres</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Stats pills */}
          <div className="flex items-center gap-1 text-[10px]">
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/20 font-bold">{stats.up} hausse</span>
            <span className="px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/20 font-bold">{stats.down} baisse</span>
            {stats.hotVol > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/20 font-bold flex items-center gap-0.5"><Volume2 className="w-2.5 h-2.5" />{stats.hotVol} volume anormal</span>}
            {stats.bestPct > 0 && <span className="px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/20 font-bold">Sprint: +{stats.bestPct.toFixed(1)}%</span>}
          </div>
          <WatchlistCsvImport owner={owner} onDone={onReload} />
          <button onClick={onRefreshPrices} className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs hover:bg-violet-500/30 transition-colors" data-testid="refresh-watchlist">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {/* Filters + Add */}
      <div className="flex items-center gap-2 flex-wrap">
        {["ALL", "US", "EU", "ASIA"].map(r => (
          <button key={r} onClick={() => setFilter(r)} className={`px-3 py-1 rounded-lg text-xs font-bold transition-colors ${filter === r ? "bg-violet-500/30 text-violet-200 border border-violet-500/40" : "bg-slate-800/40 text-slate-400 border border-slate-700/30 hover:bg-slate-700/40"}`} data-testid={`filter-${r}`}>
            {r}
          </button>
        ))}
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Filtrer..." className="pl-7 pr-2 py-1 rounded-lg bg-slate-800/40 border border-slate-700/30 text-xs text-white placeholder-slate-500 w-28 focus:outline-none focus:border-violet-500/50" />
        </div>
        <div className="flex items-center gap-1">
          <input type="text" value={addTicker} onChange={e => setAddTicker(e.target.value.toUpperCase())} onKeyDown={e => e.key === "Enter" && handleAdd()} placeholder="+ Ticker" className="pl-2 pr-2 py-1 rounded-lg bg-slate-800/40 border border-slate-700/30 text-xs text-white placeholder-slate-500 w-24 focus:outline-none focus:border-violet-500/50" data-testid="add-ticker-input" />
          <button onClick={handleAdd} className="p-1 rounded bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/30" data-testid="add-ticker-btn"><Plus className="w-3 h-3" /></button>
        </div>
      </div>

      {/* Table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="watchlist-table">
            <thead>
              <tr className="text-slate-400 border-b border-slate-700/50 text-left">
                <th className="py-2 px-2 font-medium w-8">#</th>
                <th className="py-2 px-1.5 font-medium">Ticker</th>
                <th className="py-2 px-1.5 font-medium">Prix</th>
                <SortHeader col="day_change" label="% Jour" />
                <SortHeader col="volume" label="Volume" />
                <SortHeader col="score" label="Score" />
                <SortHeader col="rsi" label="RSI" />
                <th className="py-2 px-1.5 font-medium">Signal</th>
                <th className="py-2 px-1.5 font-medium">Momentum</th>
                <th className="py-2 px-1.5 font-medium w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((w, i) => {
                const dayColor = w.dayPct > 0 ? "text-emerald-400" : w.dayPct < 0 ? "text-rose-400" : "text-slate-400";
                const signalColors = {
                  "STRONG_BUY": "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
                  "BUY": "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
                  "HOLD": "bg-slate-600/20 text-slate-300 border-slate-500/20",
                  "SELL": "bg-rose-500/10 text-rose-300 border-rose-500/20",
                  "STRONG_SELL": "bg-rose-500/20 text-rose-300 border-rose-500/30",
                  "AVOID": "bg-slate-600/20 text-slate-400 border-slate-600/20",
                };
                const badgeColors = {
                  "rose": "bg-rose-500/15 text-rose-300 border-rose-500/25",
                  "amber": "bg-amber-500/15 text-amber-300 border-amber-500/25",
                  "emerald": "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
                  "cyan": "bg-cyan-500/15 text-cyan-300 border-cyan-500/25",
                };

                return (
                  <tr key={w.ticker} className="border-b border-slate-800/30 hover:bg-slate-700/20 transition-colors cursor-pointer" onClick={() => onAnalyze?.(w.ticker)} data-testid={`watchlist-row-${w.ticker}`}>
                    <td className="py-2 px-2 text-slate-500 font-bold">{i + 1}</td>
                    <td className="py-2 px-1.5">
                      <div className="flex items-center gap-1">
                        <span className="font-mono font-bold text-cyan-400 hover:text-cyan-300">{w.ticker}</span>
                        <ExternalLink className="w-2.5 h-2.5 text-slate-600" />
                      </div>
                      <div className="text-[9px] text-slate-500 truncate max-w-[120px]">{w.name || ""}</div>
                    </td>
                    <td className="py-2 px-1.5 font-mono text-white font-bold">{w.price > 0 ? w.price.toFixed(2) : "-"}</td>
                    <td className={`py-2 px-1.5 font-mono font-bold ${dayColor}`}>
                      <div className="flex items-center gap-0.5">
                        {w.dayPct > 0 ? <TrendingUp className="w-3 h-3" /> : w.dayPct < 0 ? <TrendingDown className="w-3 h-3" /> : null}
                        {w.dayPct > 0 ? "+" : ""}{w.dayPct.toFixed(2)}%
                      </div>
                    </td>
                    <td className="py-2 px-1.5">
                      {(w.volume || w.volRatio) ? (
                        <div className="flex flex-col">
                          {w.volume ? (
                            <span className={`font-mono text-[10px] ${w.volRatio && w.volRatio >= 2 ? "text-amber-400 font-bold" : "text-slate-300"}`}>
                              {fmtVol(w.volume)}
                            </span>
                          ) : null}
                          {w.volRatio && (
                            <span className={`text-[9px] font-mono ${w.volRatio >= 2 ? "text-amber-400 font-bold" : w.volRatio >= 1.5 ? "text-amber-400/70" : "text-slate-500"}`}>
                              x{w.volRatio.toFixed(1)}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-slate-600">-</span>
                      )}
                    </td>
                    <td className="py-2 px-1.5">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-[11px] ${w.score >= 60 ? "bg-emerald-500/20 text-emerald-300" : w.score >= 40 ? "bg-amber-500/15 text-amber-300" : "bg-rose-500/15 text-rose-300"}`}>
                        {w.score}
                      </div>
                    </td>
                    <td className="py-2 px-1.5">
                      {w.rsi ? (
                        <div className={`font-mono text-[11px] ${w.rsi < 30 ? "text-emerald-400 font-bold" : w.rsi > 70 ? "text-rose-400 font-bold" : "text-slate-300"}`}>
                          {w.rsi.toFixed(0)}
                        </div>
                      ) : (
                        <span className="text-slate-600">-</span>
                      )}
                    </td>
                    <td className="py-2 px-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${signalColors[w.signal] || signalColors["HOLD"]}`}>
                        {w.signal}
                      </span>
                    </td>
                    <td className="py-2 px-1.5">
                      <div className="flex flex-wrap gap-0.5">
                        {w.badges.map((b, j) => (
                          <span key={j} className={`px-1 py-px rounded text-[8px] font-bold border ${badgeColors[b.color] || ""}`}>
                            {b.label}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 px-1.5">
                      <button onClick={(e) => { e.stopPropagation(); onRemove?.(w.ticker); }} className="p-1 rounded hover:bg-rose-500/20 text-slate-500 hover:text-rose-400 transition-colors" data-testid={`remove-${w.ticker}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12 text-sm text-slate-500">
            {search ? "Aucun ticker ne correspond" : "Watchlist vide — ajoutez des tickers"}
          </div>
        )}
      </div>
    </div>
  );
}
